/**
 * MSapling Auth Context
 * (TS Migrated)
 */

import React, { createContext, useState, useContext, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../api';
import { ROUTES } from '../routing';
import { withTimeout } from '../utils/timeout';
import { logTelemetryEvent } from '../telemetry';
import { logger } from '../utils/logger';
import { showToast } from '../utils/toast';
import { useAuthStore, type User as AuthStoreUser } from '../stores/authStore';
import { LocalStorageKey } from '../stores/globalStore';
import { guestRetryDelayMs, newIdempotencyKey } from './guestRetryDelay';

export interface User {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  is_pro: boolean;
  fuel_credits: number;
  fuel_credits_purchased?: number;
  tier: string;
  daily_free_quota?: number;
  daily_tokens_used?: number;
  storage_used_bytes: number;
  image_used_bytes: number;
  display_name?: string | null;
  username?: string | null;
  [key: string]: unknown;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  deploymentMode: string;
  // True only when boot finished and we could establish NEITHER a real user
  // session NOR a guest session — i.e. the session is expired/unavailable and
  // the user must sign in. A valid guest session is NOT a sessionExpired state.
  sessionExpired: boolean;
  login: (userData: User) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
  ensureGuestSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface PersistedAuthState {
  userId: string;
  isAuthenticated: boolean;
  deploymentMode: string;
  savedAt: string;
}


const readPersistedAuthState = (): PersistedAuthState | null => {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    try {
        const raw = window.localStorage.getItem(LocalStorageKey.AUTH_STATE);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PersistedAuthState;
        if (!parsed || typeof parsed !== 'object') return null;
        // Security: only userId + isAuthenticated are stored — no PII.
        if (!parsed.userId || typeof parsed.userId !== 'string') return null;
        return parsed;
    } catch {
        return null;
    }
};

const persistAuthState = (user: User, deploymentMode: string): void => {
    if (typeof window === 'undefined' || !window.localStorage || !user) return;
    try {
        // Security: persist ONLY userId + isAuthenticated flag.
        // No email, username, tier, or any other PII touches localStorage.
        // No authentication tokens or session credentials are stored here.
        // Full user data is always fetched live from /users/me on mount.
        // This localStorage key (msapling_auth_state_v1) is NOT a security risk
        // despite the name — it stores only non-sensitive UI state for session
        // restoration, not sensitive credentials. XSS would still be a threat to
        // userId visibility, but that's mitigated by the app's CSP and sanitization
        // of all user-controlled content.
        const payload: PersistedAuthState = {
            userId: user.id,
            isAuthenticated: true,
            deploymentMode: deploymentMode || 'full',
            savedAt: new Date().toISOString(),
        };
        const next = JSON.stringify(payload);
        // Skip the write when the stored value already matches (ignoring savedAt timestamp).
        // This avoids triggering storage events and unnecessary disk I/O on every render.
        const prev = window.localStorage.getItem(LocalStorageKey.AUTH_STATE);
        if (prev) {
            try {
                const prevObj = JSON.parse(prev);
                const { savedAt: _a, ...prevRest } = prevObj;
                const { savedAt: _b, ...nextRest } = JSON.parse(next);
                if (JSON.stringify(prevRest) === JSON.stringify(nextRest)) return;
            } catch {
                // Stored value is corrupt — overwrite it.
            }
        }
        window.localStorage.setItem(LocalStorageKey.AUTH_STATE, next);
    } catch {
        // Ignore storage failures.
    }
};

const clearPersistedAuthState = (): void => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.removeItem(LocalStorageKey.AUTH_STATE);
    } catch {
        // Ignore storage failures.
    }
};

const isAuthFailure = (error: unknown): boolean => {
    const message = String((error != null && typeof error === 'object' && 'message' in error ? (error as { message: unknown }).message : '') || '').toLowerCase();
    return message.includes('401') || message.includes('unauthorized') || message.includes('not authenticated');
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const navigate = useNavigate();
    const restoredAuthState = readPersistedAuthState();
    // Security: persisted state contains only userId + isAuthenticated — no PII.
    // Full user object is always hydrated from /users/me via checkAuth on mount.
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!restoredAuthState?.isAuthenticated);
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    // Set true only when neither a real nor guest session could be established.
    const [sessionExpired, setSessionExpired] = useState<boolean>(false);
    const [deploymentMode, setDeploymentMode] = useState<string>(() => restoredAuthState?.deploymentMode || 'full');
    // Single-flight guard: stores the in-flight promise so concurrent callers
    // wait for the same request instead of returning stale userRef.current.
    const refreshInFlightRef = useRef<Promise<User | null> | null>(null);
    const guestSessionInFlightRef = useRef<Promise<boolean> | null>(null);
    const guestRetryAfterRef = useRef(0);
    const identityGenerationRef = useRef(0);
    const guestAbortRef = useRef<AbortController | null>(null);
    useEffect(() => () => { guestAbortRef.current?.abort(); }, []);
    const logoutInFlightRef = useRef<Promise<void> | null>(null);
    // Guard: checkAuth must only run once on mount. Because checkAuth is a
    // useCallback that depends on refreshUser and createGuestSession (which
    // depend on each other), React Strict Mode double-invocation or any future
    // change to those callbacks could otherwise re-trigger the auth boot flow,
    // potentially creating a checkAuth→createGuestSession→refreshUser loop.
    const checkAuthFiredRef = useRef(false);
    // Guard: fire the "session expired → demoted to guest" toast at most once
    // per demotion event. Reset when a real (non-guest) user is loaded again.
    const sessionExpiryToastShownRef = useRef(false);
    // Ref always holds the latest user value so refreshUser never closes over
    // a stale snapshot from a previous render cycle.
    const userRef = useRef<User | null>(user);
    useEffect(() => { userRef.current = user; }, [user]);

    const refreshUser = useCallback(async (opts?: { expectUnauthenticated?: boolean }): Promise<User | null> => {
        // If a refresh is already in flight, wait for it rather than firing a
        // second request. This prevents concurrent callers from receiving a
        // stale userRef.current while the real fetch is still pending.
        if (refreshInFlightRef.current) {
            return refreshInFlightRef.current;
        }

        // expectUnauthenticated: this is the first-boot cookie probe for a
        // visitor who may have no session at all. A 401 here is EXPECTED (fresh
        // anonymous visitor — we then create a guest session) and must NOT log a
        // warning or emit a refresh_user_401 telemetry event, or every anonymous
        // first load spams the console (the harness saw users/me 401 + this warn
        // on every guest boot). A 401 on any LATER refresh is genuinely
        // noteworthy and still logged.
        const expectUnauthenticated = !!opts?.expectUnauthenticated;

        const doRefresh = async (): Promise<User | null> => {
            const generation = identityGenerationRef.current;
            const { controller, timer } = withTimeout(10000);
            try {
                const response = await fetchWithAuth(ROUTES.usersMe, {
                    signal: controller.signal,
                });

                if (generation !== identityGenerationRef.current) return userRef.current;

                if (!response.ok) {
                    if (response.status === 401) {
                        clearPersistedAuthState();
                        setUser(null);
                        setIsAuthenticated(false);
                    }
                    if (!expectUnauthenticated) {
                        logTelemetryEvent({ event: 'refresh_user_401', category: 'auth' });
                        logger.warn('refreshUser: session expired or unauthorized');
                    }
                    return null;
                }
                const userData = await response.json() as User;
                if (generation !== identityGenerationRef.current) return userRef.current;
                userRef.current = userData;
                setUser(userData);
                setIsAuthenticated(true);
                return userData;
            } catch (error: any) {
                if (generation !== identityGenerationRef.current) return userRef.current;
                if (error?.name === 'AbortError') {
                    logger.warn('Auth check timed out');
                }
                if (isAuthFailure(error)) {
                    clearPersistedAuthState();
                    setUser(null);
                    setIsAuthenticated(false);
                    return null;
                }
                // Network error (not auth failure): keep isAuthenticated hint from
                // persisted state so the UI doesn't flash to logged-out, but do NOT
                // populate user — it will be fetched on next successful refreshUser.
                const cached = readPersistedAuthState();
                if (cached?.isAuthenticated) {
                    setIsAuthenticated(true);
                    setDeploymentMode(cached.deploymentMode || 'full');
                    return null;
                }
            } finally {
                clearTimeout(timer);
                refreshInFlightRef.current = null;
            }
            setUser(null);
            setIsAuthenticated(false);
            return null;
        };

        refreshInFlightRef.current = doRefresh();
        return refreshInFlightRef.current;
    // No dependency on `user` — userRef.current always reflects the latest value.
    }, []);

    const createGuestSession = useCallback(async (): Promise<boolean> => {
        if (guestSessionInFlightRef.current) return guestSessionInFlightRef.current;
        if (Date.now() < guestRetryAfterRef.current) return false;

        const doCreate = async (): Promise<boolean> => {
            const idempotencyKey = newIdempotencyKey('auth_guest');
            const generation = identityGenerationRef.current;
            const controller = new AbortController();
            guestAbortRef.current = controller;
            try {
                const response = await fetchWithAuth(ROUTES.authGuest, {
                    method: 'POST',
                    maxAttempts: 1,
                    signal: controller.signal,
                    headers: {
                        // Enables server-side dedupe if supported; also allows safe client retries.
                        'Idempotency-Key': idempotencyKey,
                    },
                });
                if (generation !== identityGenerationRef.current) return !!userRef.current;
                if (!response.ok) {
                    if (response.status === 429) {
                        guestRetryAfterRef.current = Date.now() + guestRetryDelayMs(response.headers.get('Retry-After'));
                    }
                    return false;
                }
                // After creating session, fetch the user profile (guest profile)
                const userData = await refreshUser();
                return !!userData;
            } catch (error) {
                if (generation !== identityGenerationRef.current) return !!userRef.current;
                logger.error('Guest session failed:', error);
                logTelemetryEvent({
                    event: 'guest_session_failed',
                    category: 'auth',
                    label: String(error),
                });
                return false;
            } finally {
                if (guestAbortRef.current === controller) guestAbortRef.current = null;
                guestSessionInFlightRef.current = null;
            }
        };

        guestSessionInFlightRef.current = doCreate();
        return guestSessionInFlightRef.current!;
    }, [refreshUser]);

    const ensureGuestSession = useCallback(async (): Promise<boolean> => {
        if (user) return true;
        return createGuestSession();
    }, [createGuestSession, user]);

    // Check if cookie is valid on load
    const checkAuth = useCallback(async () => {
        try {
            // Run health check and user auth fetch in parallel to reduce time-to-interactive.
            // Both requests are independent — parallelizing saves ~150ms on typical connections.
            // For a visitor with no persisted auth (a fresh/anonymous load), the
            // initial /users/me probe is EXPECTED to 401 — suppress its warn +
            // telemetry. For a returning authenticated user, a 401 means a real
            // expiry, so keep normal logging. The probe itself still runs either
            // way: it restores an existing session (real OR guest cookie) before
            // we decide to mint a new guest, so it must NOT be skipped.
            const probeExpectsAnon = !restoredAuthState?.isAuthenticated;
            const [healthResult, userResult] = await Promise.allSettled([
                fetchWithAuth('/health').catch(() => null),
                refreshUser(probeExpectsAnon ? { expectUnauthenticated: true } : undefined),
            ]);

            // Extract deployment mode from health check if available
            if (healthResult.status === 'fulfilled' && healthResult.value?.ok) {
                const healthData = await (healthResult.value as Response).json().catch(() => ({}));
                if (healthData.deployment_mode) {
                    setDeploymentMode(healthData.deployment_mode);
                }
            }

            // Use the user result from the parallel call — no need to call refreshUser again
            const userData = userResult.status === 'fulfilled' ? userResult.value : null;
            if (userData) {
                // PII removed: do not log user email to browser console
                setSessionExpired(false);
                // Real user loaded — reset the expiry-toast guard so a future
                // demotion (e.g. after re-login then expiry) toasts again.
                sessionExpiryToastShownRef.current = false;
            } else {
                // No real session — try to establish an anonymous guest session.
                // A successful guest session is a normal logged-out-but-working
                // state and must NOT be treated as expired.
                const guestOk = await createGuestSession();
                // REGRESSION FIX: only a PREVIOUSLY-authenticated session that we
                // can no longer restore counts as "expired" (-> login wall). A
                // first-load anonymous visitor who merely failed to get a guest
                // session (e.g. a transient /auth/guest failure) is NOT expired —
                // they keep the full app, exactly as before the expired-session UX
                // change. Without this guard a guest /auth/guest 500 bounced every
                // anonymous visitor to /login (the reported regression).
                const hadPriorAuth = !!readPersistedAuthState()?.isAuthenticated;
                setSessionExpired(!guestOk && hadPriorAuth);
                // Toast-only demotion notice: when a previously-authenticated user's
                // session expires and a guest session was successfully minted, inform
                // them with a single toast. No login wall — a working guest session
                // exists and a wall would be a strictly worse UX (Opus re-audit).
                if (guestOk && hadPriorAuth && !sessionExpiryToastShownRef.current) {
                    sessionExpiryToastShownRef.current = true;
                    showToast(
                        "Your session expired — you're now browsing as a guest. Sign in to restore your account.",
                        'warn',
                        8000,
                    );
                }
            }
        } catch (error) {
            logger.error('Auth boot failed:', error);
        } finally {
            setIsLoading(false);
        }
    }, [refreshUser, createGuestSession]);

    // Check auth on mount — guarded so the boot flow only runs once even if
    // checkAuth gets a new reference (e.g. in React Strict Mode double-invoke
    // or if createGuestSession identity changes after the first render).
    useEffect(() => {
        if (checkAuthFiredRef.current) return;
        checkAuthFiredRef.current = true;
        checkAuth();
    }, [checkAuth]);

    // Auto-recover an expired session so a lapsed cookie can't 401-flood the backend.
    // When the SERVER session expires mid-use (e.g. a guest cookie hits its TTL), every
    // gated poller (active-streams, per-chat checkout, project locks) keeps firing on
    // its schedule and 401s forever, because client-side `isAuthenticated` was set at
    // boot and nothing re-establishes the session. The api client dispatches
    // `msapling:auth:expired` on any 401 — listen for it and re-probe via checkAuth,
    // which restores a still-valid real/guest cookie, mints a fresh guest for an
    // anonymous visitor, or sets the login wall for a real user who can't be restored
    // (flipping isAuthenticated=false, which gates the pollers OFF). Any outcome stops
    // the flood. Throttled by a cooldown so a burst of 401s triggers at most one
    // recovery, and a persistently failing recovery degrades to ~1 attempt/cooldown
    // instead of a hot loop (checkAuth's own /users/me 401 re-emits the event).
    const lastAuthRecoveryRef = useRef(0);
    useEffect(() => {
        const RECOVERY_COOLDOWN_MS = 15_000;
        const onExpired = () => {
            // Skip during initial boot (the mount effect handles it) and at the
            // explicit login wall (there the pollers are already gated off — no flood).
            if (isLoading || sessionExpired || Date.now() < guestRetryAfterRef.current) return;
            const now = Date.now();
            if (now - lastAuthRecoveryRef.current < RECOVERY_COOLDOWN_MS) return;
            lastAuthRecoveryRef.current = now;
            void checkAuth();
        };
        window.addEventListener('msapling:auth:expired', onExpired);
        return () => window.removeEventListener('msapling:auth:expired', onExpired);
    }, [isLoading, sessionExpired, checkAuth]);

    useEffect(() => {
        if (user && isAuthenticated) {
            persistAuthState(user, deploymentMode);
            return;
        }
        clearPersistedAuthState();
    }, [user, isAuthenticated, deploymentMode]);

    // Mirror the auth user into the Zustand auth store so it is the SINGLE
    // canonical source for any component that reads identity/fuel without prop
    // drilling (useFuelBalance, QuotaExhaustedBanner, the live CAP_STATUS sync).
    // Keeping the store fed here means `user.fuel_credits` is one value app-wide.
    useEffect(() => {
        const store = useAuthStore.getState?.();
        if (!store || typeof store.setUser !== 'function') return;
        store.setUser((user as unknown as AuthStoreUser) ?? null);
        store.setAuthenticated?.(isAuthenticated);
    }, [user, isAuthenticated]);

    // Login handler - accepts user data from Login component
    const login = (userData: User) => {
        // PII removed: do not log user email
        identityGenerationRef.current += 1;
        guestAbortRef.current?.abort();
        userRef.current = userData;
        setUser(userData);
        setIsAuthenticated(true);
        setSessionExpired(false);
        navigate('/');
    };

    // Logout handler
    const logout = async () => {
        if (logoutInFlightRef.current) return logoutInFlightRef.current;
        identityGenerationRef.current += 1;
        guestAbortRef.current?.abort();

        const doLogout = async (): Promise<void> => {
            const idempotencyKey = newIdempotencyKey('auth_logout');
            try {
                // logout initiated
                await fetchWithAuth(ROUTES.authLogout, {
                    method: 'POST',
                    headers: {
                        'Idempotency-Key': idempotencyKey,
                    },
                });
                // logout complete
            } catch (e) {
                logger.error('Logout error:', e);
            } finally {
                setUser(null);
                setIsAuthenticated(false);
                clearPersistedAuthState();
                // Clear auth loop detection state to prevent false positives after re-login
                try {
                    window.sessionStorage.removeItem("msapling_auth_401_track");
                    window.sessionStorage.removeItem("msapling_auth_401_break_ts");
                } catch { /* ignore */ }
                // Logout returns to the app root (msapling.com), not /login; the root
                // path creates a fresh guest session on initial app load.
                window.location.href = '/';
                logoutInFlightRef.current = null;
            }
        };

        logoutInFlightRef.current = doLogout();
        return logoutInFlightRef.current!;
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                isLoading,
                sessionExpired,
                deploymentMode,
                login,
                logout,
                refreshUser,
                ensureGuestSession,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
